/* oxlint-disable tailwindcss/no-unknown-classes -- Styles are colocated in home-new.css. */
import { useEffect, useRef, useState } from "react"

import { SiteSection } from "../site/SiteSection"
import { cardRingClassName } from "./homeNewStyles"

const problems = [
  {
    title: "Remembering why the code changed",
    description: "Prompts, decisions, and agent sessions kept with every commit.",
    badge: "Captured by Entire",
  },
  {
    title: "Reviewing more code than people can read",
    description: "Custom evals flag errors, so reviewers focus where it matters.",
    badge: "Reviewed by Entire",
  },
  {
    title: "Running pipelines slower than agents push",
    description: "Checks that run at agent speed, so every push gets tested without the queue.",
    badge: "Integrated with Entire",
  },
  {
    title: "Scaling your workflow for agents",
    description:
      "A decentralized network built for thousands of agents cloning and pushing at once.",
    badge: "Handled by Entire",
  },
]

// Rows settle as the reader scrolls: once a row's top crosses 60% of the way
// down the viewport it is "done" — the title is struck through, the check
// draws in and the badge sharpens into place. The last row raises into its card at the
// same point. Each row settles once and stays settled.
const SETTLE_AT_VIEWPORT = 0.6

function useSettledRows() {
  const rowRefs = useRef<Array<HTMLLIElement | null>>([])
  const [settled, setSettled] = useState<ReadonlySet<number>>(() => new Set())

  useEffect(() => {
    const rows = rowRefs.current.filter((row): row is HTMLLIElement => row !== null)
    if (rows.length === 0) return

    const pending = new Set(rows)
    let observer: IntersectionObserver | undefined

    // Shrink the root to its top 60%: a row intersects once its top crosses
    // the 60vh line. The margin is given in pixels from the viewport height
    // and recomputed on resize, since the spec resolves a percentage margin
    // against the root's width, which on a wide, short window would leave
    // almost nothing of the viewport observed.
    const observe = () => {
      observer?.disconnect()
      const next = new IntersectionObserver(
        (entries) => {
          const done = entries
            .filter((entry) => entry.isIntersecting)
            .map((entry) => rows.indexOf(entry.target as HTMLLIElement))
          if (done.length === 0) return
          setSettled((current) => {
            const settledNow = new Set(current)
            done.forEach((index) => settledNow.add(index))
            return settledNow
          })
          done.forEach((index) => {
            pending.delete(rows[index]!)
            next.unobserve(rows[index]!)
          })
        },
        {
          rootMargin: `0px 0px -${Math.round(window.innerHeight * (1 - SETTLE_AT_VIEWPORT))}px 0px`,
          threshold: 0,
        },
      )
      pending.forEach((row) => next.observe(row))
      observer = next
    }

    observe()
    window.addEventListener("resize", observe)

    return () => {
      window.removeEventListener("resize", observe)
      observer?.disconnect()
    }
  }, [])

  return { rowRefs, settled }
}

function CheckMark() {
  // Own SVG rather than lucide's so the path can carry pathLength for the draw-in.
  return (
    <svg
      viewBox="0 0 16 16"
      width={16}
      height={16}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.33}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="home-new-problem-check shrink-0 text-default"
      aria-hidden="true"
    >
      <path d="M13.333 4L6 11.333 2.667 8" pathLength={1} />
    </svg>
  )
}

export function HomeNewProblemSection() {
  const { rowRefs, settled } = useSettledRows()
  const rowProps = (index: number) => ({
    ref: (element: HTMLLIElement | null) => {
      rowRefs.current[index] = element
    },
    "data-problem": index + 1,
    "data-settled": settled.has(index) ? "true" : "false",
  })

  return (
    <SiteSection
      fullWidth
      outerClassName="border-t border-subtle"
      innerClassName="mx-auto grid max-w-320 gap-10 px-6 py-16 sm:px-10 lg:grid-cols-3 lg:gap-6 min-[1360px]:px-0"
    >
      <div className="flex flex-col gap-4">
        <p className="font-sans text-sm leading-5 font-medium text-default">The problem</p>
        <h2 className="font-headline text-[32px] leading-none font-medium text-default">
          Source Control is at a Breaking Point
        </h2>
        <p className="max-w-[344px] font-sans text-base leading-5 text-muted">
          Agents waste tokens searching for answers. There’s too much code to review. Agents push
          faster than pipelines run. And infrastructure can’t scale for the agentic era.
        </p>
      </div>

      {/* The plain rows sit flush on the section; only the last row is a
          raised, highlighted card. */}
      <ul className="flex flex-col gap-2 min-[1360px]:px-20 lg:col-span-2">
        {problems.map((problem, index) => (
          <li
            key={problem.title}
            {...rowProps(index)}
            className="home-new-problem-row flex min-h-20 flex-wrap items-center gap-x-4 gap-y-3 rounded-lg p-4"
          >
            <CheckMark />
            <div className="flex min-w-52 flex-1 flex-col gap-0.5 font-sans text-sm leading-5 font-medium">
              <p className="home-new-problem-title text-default">{problem.title}</p>
              <p className="text-muted">{problem.description}</p>
            </div>
            <span
              className={`home-new-problem-badge flex h-6 shrink-0 items-center rounded-sm bg-floating px-1.5 pb-px font-sans text-xs leading-[15px] font-medium whitespace-nowrap text-default max-sm:ml-8 ${cardRingClassName}`}
            >
              {problem.badge}
            </span>
          </li>
        ))}
        <li
          {...rowProps(problems.length)}
          className="home-new-problem-row home-new-problem-row-raised mt-2 flex min-h-20 flex-wrap items-center gap-x-4 gap-y-3 rounded-lg p-4"
        >
          <CheckMark />
          <div className="flex min-w-52 flex-1 flex-col gap-0.5 font-sans text-sm leading-5 font-medium">
            <p className="text-default">Deciding what’s worth building</p>
            <p className="text-muted">Your ideas, your judgment, your next big thing.</p>
          </div>
          <span className="home-new-problem-badge flex h-6 shrink-0 items-center rounded-sm bg-(--home-new-badge) px-1.5 pb-px font-sans text-xs leading-[15px] font-medium whitespace-nowrap text-white max-sm:ml-8 dark:bg-[color-mix(in_srgb,var(--home-new-badge)_65%,black)]">
            Yours again
          </span>
        </li>
      </ul>
    </SiteSection>
  )
}
